import React, { useEffect, useState, useRef } from "react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Wallet, TrendingUp, TrendingDown, Trophy, History, ArrowUpRight, Loader2, BarChart3, Award, Clock } from "lucide-react";
import { useTheme } from "../context/ThemeContext";

const NAV_ITEMS = [
  { id: "portfolio", label: "Portfolio", icon: BarChart3 },
  { id: "trade", label: "Trade", icon: ArrowUpRight },
  { id: "monthly", label: "Monthly Challenge", icon: Award },
  { id: "leaderboard", label: "Leaderboard", icon: Trophy },
  { id: "history", label: "History", icon: History },
];

function BalanceCard({ portfolio }) {
  if (!portfolio) {
    return (
      <div className="bg-gradient-to-br from-yellow-400/10 to-transparent border border-yellow-400/20 rounded-xl p-5">
        <div className="text-xs font-mono uppercase tracking-widest text-[var(--text-tertiary)]">Total Portfolio Value</div>
        <div className="h-8 w-32 bg-[var(--bg-elevated)] rounded mt-2 animate-pulse" />
      </div>
    );
  }
  const isUp = portfolio.total_return_pct >= 0;
  const invested = portfolio.total_value - portfolio.cash_balance;

  return (
    <div className="bg-gradient-to-br from-yellow-400/10 to-transparent border border-yellow-400/20 rounded-xl p-5 sticky top-24">
      <div className="text-xs font-mono uppercase tracking-widest text-[var(--text-tertiary)]">Total Portfolio Value</div>
      <div className="font-display font-bold text-2xl text-[var(--text-primary)] mt-1">
        ${portfolio.total_value.toLocaleString(undefined, { maximumFractionDigits: 2 })}
      </div>
      <div className={"flex items-center gap-1 text-sm font-bold mt-1 " + (isUp ? "text-green-400" : "text-red-400")}>
        {isUp ? <TrendingUp className="w-3.5 h-3.5" /> : <TrendingDown className="w-3.5 h-3.5" />}
        {isUp ? "+" : ""}{portfolio.total_return_pct}% all-time
      </div>

      <div className="flex justify-between text-xs font-mono mt-4 pt-3 border-t border-[var(--border-soft)]">
        <span className="text-[var(--text-tertiary)]">Cash Available</span>
        <span className="text-[var(--text-primary)] font-bold">${portfolio.cash_balance.toLocaleString(undefined, { maximumFractionDigits: 2 })}</span>
      </div>
      <div className="flex justify-between text-xs font-mono mt-2">
        <span className="text-[var(--text-tertiary)]">Invested</span>
        <span className="text-[var(--text-primary)] font-bold">${invested.toLocaleString(undefined, { maximumFractionDigits: 2 })}</span>
      </div>
      <div className="flex justify-between text-xs font-mono mt-2">
        <span className="text-[var(--text-tertiary)]">Starting Balance</span>
        <span className="text-[var(--text-tertiary)]">${portfolio.starting_balance.toLocaleString()}</span>
      </div>
    </div>
  );
}

function PortfolioTab({ portfolio, loading }) {
  if (loading) return <p className="text-center text-[var(--text-tertiary)] font-mono text-sm py-10">Loading portfolio...</p>;
  if (!portfolio) return null;

  if (portfolio.holdings.length === 0) {
    return (
      <div className="text-center py-16 border border-[var(--border-soft)] bg-[var(--bg-panel)] rounded-xl">
        <p className="text-[var(--text-tertiary)] font-mono text-sm">No holdings yet. Head to the Trade tab to make your first move.</p>
      </div>
    );
  }

  return (
    <div className="bg-[var(--bg-panel)] border border-[var(--border-soft)] rounded-xl overflow-hidden overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left border-b border-[var(--border-soft)] text-xs font-mono uppercase tracking-widest text-[var(--text-tertiary)]">
            <th className="p-4">Asset</th>
            <th className="p-4 text-right">Quantity</th>
            <th className="p-4 text-right">Avg Cost</th>
            <th className="p-4 text-right">Price</th>
            <th className="p-4 text-right">Value</th>
            <th className="p-4 text-right">P&L</th>
          </tr>
        </thead>
        <tbody>
          {portfolio.holdings.map((h) => {
            const pnlUp = h.pnl >= 0;
            return (
              <tr key={h.symbol + h.asset_type} className="border-b border-[var(--border-soft)] last:border-0 hover:bg-[var(--bg-elevated)] transition">
                <td className="p-4">
                  <div className="font-bold text-[var(--text-primary)]">{h.symbol}</div>
                  <div className="text-xs font-mono text-[var(--text-tertiary)] uppercase">{h.asset_type}</div>
                </td>
                <td className="p-4 text-right font-mono text-xs text-[var(--text-secondary)]">{h.quantity.toFixed(6)}</td>
                <td className="p-4 text-right font-mono text-xs text-[var(--text-secondary)]">${h.avg_cost.toFixed(2)}</td>
                <td className="p-4 text-right font-mono text-xs text-[var(--text-primary)]">{h.current_price ? "$" + h.current_price.toFixed(2) : "—"}</td>
                <td className="p-4 text-right font-mono text-sm text-[var(--text-primary)]">{h.value ? "$" + h.value.toLocaleString(undefined, { maximumFractionDigits: 2 }) : "—"}</td>
                <td className="p-4 text-right">
                  {h.pnl != null && (
                    <span className={"font-mono text-xs font-bold " + (pnlUp ? "text-green-400" : "text-red-400")}>
                      {pnlUp ? "+" : ""}${Math.abs(h.pnl).toLocaleString(undefined, { maximumFractionDigits: 2 })} ({pnlUp ? "+" : ""}{h.pnl_pct}%)
                    </span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function TradeTab({ assets, portfolio, onTraded }) {
  const [side, setSide] = useState("buy");
  const [assetType, setAssetType] = useState("crypto");
  const [symbol, setSymbol] = useState("");
  const [amountUsd, setAmountUsd] = useState("");
  const [quantity, setQuantity] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const symbolList = assetType === "crypto" ? assets.crypto : assetType === "stock" ? assets.stocks : assets.commodities;
  const owned = portfolio?.holdings.filter((h) => h.asset_type === assetType) || [];

  useEffect(() => {
    if (side === "buy" && symbolList.length > 0) setSymbol(symbolList[0]);
    else if (side === "sell" && owned.length > 0) setSymbol(owned[0].symbol);
    else setSymbol("");
  }, [side, assetType]);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (side === "buy") {
        const amt = parseFloat(amountUsd);
        if (!amt || amt <= 0) { toast.error("Enter a valid USD amount"); return; }
        const { data } = await api.post("/virtual-portfolio/buy", { symbol, asset_type: assetType, amount_usd: amt });
        toast.success(`Bought ${data.quantity} ${symbol} at $${data.price}`);
        setAmountUsd("");
      } else {
        const qty = parseFloat(quantity);
        if (!qty || qty <= 0) { toast.error("Enter a valid quantity"); return; }
        const { data } = await api.post("/virtual-portfolio/sell", { symbol, asset_type: assetType, quantity: qty });
        toast.success(`Sold for $${data.proceeds} at $${data.price}`);
        setQuantity("");
      }
      onTraded();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Trade failed");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="max-w-md">
      <div className="flex items-center gap-2 mb-6">
        <button onClick={() => setSide("buy")}
          className={"flex-1 py-3 rounded-lg text-sm font-bold uppercase tracking-wide transition " + (side === "buy" ? "bg-green-500 text-white" : "bg-[var(--bg-elevated)] text-[var(--text-tertiary)] border border-[var(--border-soft)]")}>
          Buy
        </button>
        <button onClick={() => setSide("sell")}
          className={"flex-1 py-3 rounded-lg text-sm font-bold uppercase tracking-wide transition " + (side === "sell" ? "bg-red-500 text-white" : "bg-[var(--bg-elevated)] text-[var(--text-tertiary)] border border-[var(--border-soft)]")}>
          Sell
        </button>
      </div>

      <form onSubmit={submit} className="bg-[var(--bg-panel)] border border-[var(--border-soft)] rounded-xl p-6 space-y-4">
        <div>
          <label className="text-xs font-mono text-[var(--text-tertiary)] uppercase mb-1 block">Asset Type</label>
          <div className="flex gap-2">
            {["crypto", "stock", "commodity"].map((t) => (
              <button key={t} type="button" onClick={() => setAssetType(t)}
                className={"flex-1 py-2 rounded-lg text-xs font-mono uppercase border transition " + (assetType === t ? "bg-yellow-400 text-black border-yellow-400" : "border-[var(--border-soft)] text-[var(--text-tertiary)]")}>
                {t}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="text-xs font-mono text-[var(--text-tertiary)] uppercase mb-1 block">Asset</label>
          {(side === "buy" ? symbolList : owned.map((h) => h.symbol)).length === 0 ? (
            <p className="text-xs text-[var(--text-tertiary)] py-2">
              {side === "sell" ? "You don't own any " + assetType + " assets yet." : "No assets available."}
            </p>
          ) : (
            <select value={symbol} onChange={(e) => setSymbol(e.target.value)}
              className="w-full bg-[var(--bg-elevated)] border border-[var(--border-soft)] rounded-lg px-3 py-2.5 text-sm text-[var(--text-primary)] focus:outline-none focus:border-yellow-400">
              {(side === "buy" ? symbolList : owned.map((h) => h.symbol)).map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          )}
        </div>

        {side === "buy" ? (
          <div>
            <label className="text-xs font-mono text-[var(--text-tertiary)] uppercase mb-1 block">Amount (USD)</label>
            <input type="number" value={amountUsd} onChange={(e) => setAmountUsd(e.target.value)} placeholder="e.g. 500"
              className="w-full bg-[var(--bg-elevated)] border border-[var(--border-soft)] rounded-lg px-3 py-2.5 text-sm text-[var(--text-primary)] focus:outline-none focus:border-yellow-400" />
            {portfolio && <p className="text-xs text-[var(--text-tertiary)] mt-1">Available: ${portfolio.cash_balance.toLocaleString()}</p>}
          </div>
        ) : (
          <div>
            <label className="text-xs font-mono text-[var(--text-tertiary)] uppercase mb-1 block">Quantity</label>
            <input type="number" step="any" value={quantity} onChange={(e) => setQuantity(e.target.value)} placeholder="e.g. 0.5"
              className="w-full bg-[var(--bg-elevated)] border border-[var(--border-soft)] rounded-lg px-3 py-2.5 text-sm text-[var(--text-primary)] focus:outline-none focus:border-yellow-400" />
            {owned.find((h) => h.symbol === symbol) && (
              <p className="text-xs text-[var(--text-tertiary)] mt-1">You own: {owned.find((h) => h.symbol === symbol).quantity.toFixed(6)}</p>
            )}
          </div>
        )}

        <button type="submit" disabled={submitting || !symbol}
          className={"w-full py-3 rounded-lg font-bold uppercase tracking-wide text-sm transition disabled:opacity-50 flex items-center justify-center gap-2 " +
            (side === "buy" ? "bg-green-500 hover:bg-green-600 text-white" : "bg-red-500 hover:bg-red-600 text-white")}>
          {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
          {side === "buy" ? "Buy" : "Sell"} {symbol}
        </button>
      </form>
    </div>
  );
}

function LeaderboardTab() {
  const [leaders, setLeaders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/virtual-portfolio/leaderboard").then((r) => setLeaders(r.data || [])).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) return <p className="text-center text-[var(--text-tertiary)] font-mono text-sm py-10">Loading leaderboard...</p>;
  if (leaders.length === 0) return <p className="text-center text-[var(--text-tertiary)] font-mono text-sm py-10">No traders yet. Be the first.</p>;

  const medals = ["🥇", "🥈", "🥉"];

  return (
    <div className="bg-[var(--bg-panel)] border border-[var(--border-soft)] rounded-xl overflow-hidden">
      {leaders.map((l, i) => {
        const up = l.return_pct >= 0;
        return (
          <Link key={l.user_id} to={"/profile/" + l.user_id}
            className="flex items-center justify-between p-4 border-b border-[var(--border-soft)] last:border-0 hover:bg-[var(--bg-elevated)] transition">
            <div className="flex items-center gap-3">
              <span className="text-lg w-8 text-center">{i < 3 ? medals[i] : "#" + (i + 1)}</span>
              {l.avatar_url ? (
                <img src={l.avatar_url} alt={l.name} className="w-9 h-9 rounded-full object-cover" />
              ) : (
                <div className="w-9 h-9 rounded-full bg-yellow-400/10 border border-yellow-400/30 flex items-center justify-center">
                  <span className="text-yellow-400 font-bold text-sm">{l.name[0]}</span>
                </div>
              )}
              <span className="font-bold text-sm text-[var(--text-primary)]">{l.name}</span>
            </div>
            <div className="text-right">
              <div className="font-mono font-bold text-[var(--text-primary)]">${l.total_value.toLocaleString(undefined, { maximumFractionDigits: 2 })}</div>
              <div className={"font-mono text-xs font-bold " + (up ? "text-green-400" : "text-red-400")}>
                {up ? "+" : ""}{l.return_pct}%
              </div>
            </div>
          </Link>
        );
      })}
    </div>
  );
}

function HistoryTab() {
  const [trades, setTrades] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/virtual-portfolio/trades").then((r) => setTrades(r.data || [])).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) return <p className="text-center text-[var(--text-tertiary)] font-mono text-sm py-10">Loading history...</p>;
  if (trades.length === 0) return <p className="text-center text-[var(--text-tertiary)] font-mono text-sm py-10">No trades yet.</p>;

  return (
    <div className="space-y-2">
      {trades.map((t) => (
        <div key={t.id} className="flex items-center justify-between bg-[var(--bg-panel)] border border-[var(--border-soft)] rounded-lg p-4">
          <div className="flex items-center gap-3">
            <span className={"text-xs font-mono font-bold px-2 py-1 rounded uppercase " + (t.side === "buy" ? "bg-green-500/15 text-green-400" : "bg-red-500/15 text-red-400")}>
              {t.side}
            </span>
            <div>
              <div className="font-bold text-sm text-[var(--text-primary)]">{t.symbol}</div>
              <div className="text-xs font-mono text-[var(--text-tertiary)]">{new Date(t.created_at).toLocaleString()}</div>
            </div>
          </div>
          <div className="text-right">
            <div className="font-mono text-sm text-[var(--text-primary)]">${t.amount_usd.toLocaleString()}</div>
            <div className="text-xs font-mono text-[var(--text-tertiary)]">@ ${t.price.toFixed(2)}</div>
          </div>
        </div>
      ))}
    </div>
  );
}


const TIMEFRAMES = [
  { label: "1m", interval: "1m" },
  { label: "5m", interval: "5m" },
  { label: "15m", interval: "15m" },
  { label: "1h", interval: "1h" },
  { label: "4h", interval: "4h" },
  { label: "1D", interval: "1d" },
];

function LeverageChart({ symbol, assetType, onLatestCandle }) {
  const { theme } = useTheme();
  const chartRef = useRef(null);
  const chartInstance = useRef(null);
  const candleSeriesRef = useRef(null);
  const volumeSeriesRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [timeframe, setTimeframe] = useState("1h");

  useEffect(() => {
    if (!chartRef.current) return;
    let chart;
    const timer = setTimeout(() => {
      if (!chartRef.current) return;
      import("lightweight-charts").then(({ createChart }) => {
        if (!chartRef.current) return;
        const w = chartRef.current.getBoundingClientRect().width || 600;
        const isDark = theme !== "light";
        const bgColor = isDark ? "#0a0a0b" : "#ffffff";
        const textColor = isDark ? "#a1a1aa" : "#52525b";
        const gridColor = isDark ? "#ffffff08" : "#00000008";
        const borderColor = isDark ? "#ffffff20" : "#00000020";
        chart = createChart(chartRef.current, {
          width: w,
          height: 340,
          layout: { background: { color: bgColor }, textColor: textColor },
          grid: { vertLines: { color: gridColor }, horzLines: { color: gridColor } },
          crosshair: { mode: 1 },
          rightPriceScale: { borderColor: borderColor },
          timeScale: { borderColor: borderColor, timeVisible: true },
        });
        const candleSeries = chart.addCandlestickSeries({
          upColor: "#22c55e", downColor: "#ef4444",
          borderUpColor: "#22c55e", borderDownColor: "#ef4444",
          wickUpColor: "#22c55e", wickDownColor: "#ef4444",
        });
        candleSeriesRef.current = candleSeries;
        const volSeries = chart.addHistogramSeries({
          color: "#facc1540", priceFormat: { type: "volume" }, priceScaleId: "volume",
        });
        chart.priceScale("volume").applyOptions({ scaleMargins: { top: 0.8, bottom: 0 } });
        volumeSeriesRef.current = volSeries;
        chartInstance.current = chart;
        const handleResize = () => {
          if (chartRef.current && chart) chart.applyOptions({ width: chartRef.current.getBoundingClientRect().width });
        };
        window.addEventListener("resize", handleResize);
      });
    }, 50);
    return () => {
      clearTimeout(timer);
      if (chart) chart.remove();
      chartInstance.current = null;
      candleSeriesRef.current = null;
      volumeSeriesRef.current = null;
    };
  }, [symbol, theme]);

  useEffect(() => {
    if (!symbol) return;
    setLoading(true);
    const timer = setTimeout(() => {
      if (!candleSeriesRef.current) return;
      const load = async () => {
        try {
          let candles = [];
          let volumes = [];
          if (assetType === "crypto") {
            const binanceSymbol = symbol + "USDT";
            const r = await fetch("https://api.binance.com/api/v3/klines?symbol=" + binanceSymbol + "&interval=" + timeframe + "&limit=200");
            const data = await r.json();
            if (Array.isArray(data)) {
              candles = data.map((d) => ({ time: Math.floor(d[0] / 1000), open: parseFloat(d[1]), high: parseFloat(d[2]), low: parseFloat(d[3]), close: parseFloat(d[4]) }));
              volumes = data.map((d) => ({ time: Math.floor(d[0] / 1000), value: parseFloat(d[5]), color: parseFloat(d[4]) >= parseFloat(d[1]) ? "#22c55e40" : "#ef444440" }));
            }
          } else {
            const endpoint = assetType === "stock" ? "/stocks/" + symbol + "/history" : "/commodities/" + symbol + "/history";
            const r = await api.get(endpoint, { params: { range: "90d" } });
            const rows = r.data || [];
            candles = rows.map((p) => ({ time: Math.floor(new Date(p.date + "T00:00:00Z").getTime() / 1000), open: p.open, high: p.high, low: p.low, close: p.close }));
            volumes = rows.map((p) => ({ time: Math.floor(new Date(p.date + "T00:00:00Z").getTime() / 1000), value: p.volume || 0, color: p.close >= p.open ? "#22c55e40" : "#ef444440" }));
          }
          if (candleSeriesRef.current && candles.length > 0) {
            candleSeriesRef.current.setData(candles);
            if (volumeSeriesRef.current) volumeSeriesRef.current.setData(volumes);
            chartInstance.current?.timeScale().fitContent();
            const last = candles[candles.length - 1];
            if (onLatestCandle) onLatestCandle(last);
          }
        } catch (_) {}
        finally { setLoading(false); }
      };
      load();
    }, 100);
    return () => clearTimeout(timer);
  }, [symbol, assetType, timeframe]);

  return (
    <div>
      <div className="flex items-center gap-1 mb-3">
        {TIMEFRAMES.map((t) => (
          <button key={t.label} onClick={() => assetType === "crypto" && setTimeframe(t.interval)}
            disabled={assetType !== "crypto"}
            className={"px-2.5 py-1 text-xs font-mono rounded transition disabled:opacity-30 disabled:cursor-not-allowed " +
              (timeframe === t.interval ? "bg-yellow-400 text-black font-bold" : "text-[var(--text-tertiary)] hover:text-[var(--text-primary)]")}>
            {t.label}
          </button>
        ))}
        {assetType !== "crypto" && <span className="text-xs text-[var(--text-tertiary)] ml-1">(daily bars only)</span>}
      </div>
      <div className="relative">
        {loading && (
          <div className="absolute inset-0 flex items-center justify-center text-[var(--text-tertiary)] font-mono text-sm bg-[var(--bg-panel)] z-10">
            Loading chart...
          </div>
        )}
        <div ref={chartRef} style={{ width: "100%", height: "340px" }} />
      </div>
    </div>
  );
}


function TradeHistoryPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/monthly-challenge/trade-history").then((r) => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) return <p className="text-center text-[var(--text-tertiary)] font-mono text-sm py-6">Loading trade history...</p>;
  if (!data || data.trades.length === 0) return <p className="text-center text-[var(--text-tertiary)] font-mono text-sm py-6">No closed trades yet.</p>;

  const { trades, stats } = data;
  const REASON_LABELS = { manual: "Manual", liquidated: "Liquidated", stop_loss: "Stop Loss", take_profit: "Take Profit" };
  const REASON_COLORS = { manual: "text-[var(--text-tertiary)]", liquidated: "text-red-400", stop_loss: "text-orange-400", take_profit: "text-green-400" };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-[var(--bg-elevated)] rounded-lg p-3">
          <div className="text-xs font-mono text-[var(--text-tertiary)] uppercase">Win Rate</div>
          <div className="font-display font-bold text-lg text-[var(--text-primary)]">{stats.win_rate}%</div>
        </div>
        <div className="bg-[var(--bg-elevated)] rounded-lg p-3">
          <div className="text-xs font-mono text-[var(--text-tertiary)] uppercase">Total P&L</div>
          <div className={"font-display font-bold text-lg " + (stats.total_pnl >= 0 ? "text-green-400" : "text-red-400")}>
            {stats.total_pnl >= 0 ? "+" : ""}${stats.total_pnl.toFixed(2)}
          </div>
        </div>
        <div className="bg-[var(--bg-elevated)] rounded-lg p-3">
          <div className="text-xs font-mono text-[var(--text-tertiary)] uppercase">Best Trade</div>
          <div className="font-display font-bold text-sm text-green-400">{stats.best_trade.symbol} +${stats.best_trade.pnl.toFixed(0)}</div>
        </div>
        <div className="bg-[var(--bg-elevated)] rounded-lg p-3">
          <div className="text-xs font-mono text-[var(--text-tertiary)] uppercase">Worst Trade</div>
          <div className="font-display font-bold text-sm text-red-400">{stats.worst_trade.symbol} ${stats.worst_trade.pnl.toFixed(0)}</div>
        </div>
      </div>

      <div className="space-y-2">
        {trades.map((t) => {
          const pnlUp = t.pnl >= 0;
          return (
            <div key={t.id} className="bg-[var(--bg-elevated)] rounded-lg p-3 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className={"text-xs font-bold px-2 py-0.5 rounded uppercase " + (t.side === "long" ? "bg-green-500/15 text-green-400" : "bg-red-500/15 text-red-400")}>
                  {t.side}
                </span>
                <span className="font-bold text-sm text-[var(--text-primary)]">{t.symbol}</span>
                <span className="text-xs font-mono text-yellow-400">{t.leverage}x</span>
                <span className={"text-xs font-mono " + REASON_COLORS[t.reason]}>{REASON_LABELS[t.reason]}</span>
              </div>
              <div className="text-right">
                <div className={"font-mono text-sm font-bold " + (pnlUp ? "text-green-400" : "text-red-400")}>
                  {pnlUp ? "+" : ""}${t.pnl.toFixed(2)} ({pnlUp ? "+" : ""}{t.pnl_pct}%)
                </div>
                <div className="text-xs font-mono text-[var(--text-tertiary)]">{new Date(t.closed_at).toLocaleDateString()}</div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}



function LeverageMainContent({ assetType, symbol, positions, bottomTab, setBottomTab, closePosition, closingId, currentPrice, priceChangePct, latestCandle, setLatestCandle }) {
  const isUpDay = priceChangePct >= 0;
  return (
    <div className="bg-[var(--bg-panel)] border border-[var(--border-soft)] rounded-xl p-5">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div className="flex items-center gap-3">
          <h3 className="font-display font-bold text-lg text-[var(--text-primary)]">{symbol || "—"}/USD</h3>
          {currentPrice && (
            <>
              <span className="font-mono text-xl font-bold text-[var(--text-primary)]">
                {currentPrice.toLocaleString(undefined, { maximumFractionDigits: currentPrice < 10 ? 4 : 2 })}
              </span>
              {priceChangePct != null && (
                <span className={"font-mono text-sm font-bold " + (isUpDay ? "text-green-400" : "text-red-400")}>
                  {isUpDay ? "+" : ""}{priceChangePct.toFixed(2)}%
                </span>
              )}
            </>
          )}
        </div>
        {latestCandle && (
          <div className="flex items-center gap-4 text-xs font-mono text-[var(--text-tertiary)]">
            <span>O <span className="text-[var(--text-primary)]">{latestCandle.open?.toFixed(2)}</span></span>
            <span>H <span className="text-green-400">{latestCandle.high?.toFixed(2)}</span></span>
            <span>L <span className="text-red-400">{latestCandle.low?.toFixed(2)}</span></span>
            <span>C <span className="text-[var(--text-primary)]">{latestCandle.close?.toFixed(2)}</span></span>
          </div>
        )}
      </div>
      {symbol && <LeverageChart symbol={symbol} assetType={assetType} onLatestCandle={setLatestCandle} />}

      <div className="flex items-center gap-1 mt-4 border-b border-[var(--border-soft)]">
        {[
          { id: "orders", label: "Open Orders (0)" },
          { id: "position", label: "Position (" + positions.length + ")" },
          { id: "orderhistory", label: "Order History" },
          { id: "tradehistory", label: "Trade History" },
        ].map((t) => (
          <button key={t.id} onClick={() => setBottomTab(t.id)}
            className={"px-3 py-2 text-xs font-mono uppercase tracking-wide border-b-2 transition -mb-px " +
              (bottomTab === t.id ? "text-yellow-400 border-yellow-400" : "text-[var(--text-tertiary)] border-transparent hover:text-[var(--text-primary)]")}>
            {t.label}
          </button>
        ))}
      </div>

      <div className="pt-4">
        {bottomTab === "position" && (
          positions.length === 0 ? (
            <p className="text-center text-[var(--text-tertiary)] font-mono text-sm py-6">No open leveraged positions.</p>
          ) : (
            <div className="space-y-2">
              {positions.map((p) => {
                const pnlUp = p.pnl >= 0;
                return (
                  <div key={p.id} className="bg-[var(--bg-elevated)] rounded-lg p-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className={"text-xs font-bold px-2 py-0.5 rounded uppercase " + (p.side === "long" ? "bg-green-500/15 text-green-400" : "bg-red-500/15 text-red-400")}>
                          {p.side}
                        </span>
                        <span className="font-bold text-sm text-[var(--text-primary)]">{p.symbol}</span>
                        <span className="text-xs font-mono text-yellow-400">{p.leverage}x</span>
                      </div>
                      <button onClick={() => closePosition(p.id)} disabled={closingId === p.id}
                        className="text-xs font-mono px-3 py-1 border border-[var(--border-soft)] rounded-full text-[var(--text-tertiary)] hover:border-red-400 hover:text-red-400 transition disabled:opacity-50">
                        {closingId === p.id ? "Closing..." : "Close"}
                      </button>
                    </div>
                    <div className="grid grid-cols-3 gap-2 mt-2 text-xs font-mono">
                      <div><div className="text-[var(--text-tertiary)]">Margin</div><div className="text-[var(--text-primary)]">${p.margin.toLocaleString()}</div></div>
                      <div><div className="text-[var(--text-tertiary)]">P&L</div><div className={pnlUp ? "text-green-400 font-bold" : "text-red-400 font-bold"}>{pnlUp ? "+" : ""}${p.pnl?.toFixed(2)} ({pnlUp ? "+" : ""}{p.pnl_pct}%)</div></div>
                      <div><div className="text-[var(--text-tertiary)]">Liq. Price</div><div className="text-red-400">${p.liquidation_price?.toLocaleString()}</div></div>
                    </div>
                  </div>
                );
              })}
            </div>
          )
        )}
        {(bottomTab === "orders" || bottomTab === "orderhistory") && (
          <p className="text-center text-[var(--text-tertiary)] font-mono text-sm py-6">Coming soon.</p>
        )}
        {bottomTab === "tradehistory" && <TradeHistoryPanel />}
      </div>
    </div>
  );
}

function LeverageOrderPanel(props) {
  const { assetType, setAssetType, symbol, setSymbol, symbolList, side, setSide, orderType, setOrderType,
    margin, setMargin, leverage, setLeverage, cashBalance, currentPrice, unrealizedPnl, equity, submitting, onSubmit,
    stopLoss, setStopLoss, takeProfit, setTakeProfit } = props;
  const { user } = useAuth();

  if (!user) {
    return (
      <div className="bg-[var(--bg-panel)] border border-[var(--border-soft)] rounded-xl p-6 text-center">
        <Wallet className="w-8 h-8 text-yellow-400 mx-auto mb-3" />
        <h3 className="font-display font-bold text-lg text-[var(--text-primary)] mb-2">Sign in to trade</h3>
        <p className="text-sm text-[var(--text-secondary)] mb-5">Get $10,000 in virtual cash to compete in this month's challenge.</p>
        <Link to="/register" className="block bg-yellow-400 text-black font-bold uppercase tracking-wide rounded-full px-4 py-2.5 hover:bg-yellow-500 transition text-sm mb-2">
          Join Free
        </Link>
        <Link to="/login" className="block text-xs font-mono text-[var(--text-tertiary)] hover:text-yellow-400 transition">
          Already have an account? Sign in
        </Link>
      </div>
    );
  }

  const LEVERAGE_PRESETS = [1, 5, 10, 20];
  const marginNum = parseFloat(margin) || 0;
  const liqPrice = currentPrice && marginNum > 0
    ? (side === "long" ? currentPrice * (1 - 1 / leverage) : currentPrice * (1 + 1 / leverage))
    : null;
  const estPnl10 = marginNum > 0 ? marginNum * leverage * 0.10 : 0;
  const setMarginPct = (pct) => setMargin(((cashBalance * pct) / 100).toFixed(2));

  return (
    <div className="space-y-4">
      <form onSubmit={onSubmit} className="bg-[var(--bg-panel)] border border-[var(--border-soft)] rounded-xl p-5 space-y-4">
        <div className="flex rounded-lg overflow-hidden border border-[var(--border-soft)]">
          <button type="button" onClick={() => setSide("long")}
            className={"flex-1 py-2.5 text-xs font-bold uppercase tracking-wide transition " + (side === "long" ? "bg-green-500 text-white" : "bg-[var(--bg-elevated)] text-[var(--text-tertiary)]")}>
            Long
          </button>
          <button type="button" onClick={() => setSide("short")}
            className={"flex-1 py-2.5 text-xs font-bold uppercase tracking-wide transition " + (side === "short" ? "bg-red-500 text-white" : "bg-[var(--bg-elevated)] text-[var(--text-tertiary)]")}>
            Short
          </button>
        </div>

        <div className="flex gap-2">
          {["crypto", "stock", "commodity"].map((t) => (
            <button key={t} type="button" onClick={() => setAssetType(t)}
              className={"flex-1 py-1.5 rounded-lg text-xs font-mono uppercase border transition " + (assetType === t ? "bg-yellow-400 text-black border-yellow-400" : "border-[var(--border-soft)] text-[var(--text-tertiary)]")}>
              {t}
            </button>
          ))}
        </div>

        <select value={symbol} onChange={(e) => setSymbol(e.target.value)}
          className="w-full bg-[var(--bg-elevated)] border border-[var(--border-soft)] rounded-lg px-3 py-2 text-sm text-[var(--text-primary)] focus:outline-none focus:border-yellow-400">
          {symbolList.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>

        <div>
          <label className="text-xs font-mono text-[var(--text-tertiary)] uppercase mb-1 block">Leverage</label>
          <select value={leverage} onChange={(e) => setLeverage(parseInt(e.target.value))}
            className="w-full bg-[var(--bg-elevated)] border border-[var(--border-soft)] rounded-lg px-3 py-2 text-sm text-[var(--text-primary)] focus:outline-none focus:border-yellow-400 mb-2">
            {Array.from({ length: 20 }, (_, i) => i + 1).map((lv) => <option key={lv} value={lv}>{lv}x</option>)}
          </select>
          <div className="flex gap-1.5">
            {LEVERAGE_PRESETS.map((lv) => (
              <button key={lv} type="button" onClick={() => setLeverage(lv)}
                className={"flex-1 py-1.5 rounded-lg text-xs font-mono border transition " + (leverage === lv ? "bg-yellow-400 text-black border-yellow-400" : "border-[var(--border-soft)] text-[var(--text-tertiary)]")}>
                {lv}x
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="text-xs font-mono text-[var(--text-tertiary)] uppercase mb-1 block">Order Type</label>
          <div className="flex gap-1.5">
            <button type="button" onClick={() => setOrderType("market")}
              className={"flex-1 py-1.5 rounded-lg text-xs font-mono uppercase border transition " + (orderType === "market" ? "bg-yellow-400 text-black border-yellow-400" : "border-[var(--border-soft)] text-[var(--text-tertiary)]")}>
              Market
            </button>
            <button type="button" disabled title="Coming soon"
              className="flex-1 py-1.5 rounded-lg text-xs font-mono uppercase border border-[var(--border-soft)] text-[var(--text-tertiary)] opacity-40 cursor-not-allowed">
              Limit
            </button>
            <button type="button" disabled title="Coming soon"
              className="flex-1 py-1.5 rounded-lg text-xs font-mono uppercase border border-[var(--border-soft)] text-[var(--text-tertiary)] opacity-40 cursor-not-allowed">
              Stop
            </button>
          </div>
        </div>

        <div>
          <label className="text-xs font-mono text-[var(--text-tertiary)] uppercase mb-1 block">Margin (USD)</label>
          <input type="number" value={margin} onChange={(e) => setMargin(e.target.value)} placeholder="0.00"
            className="w-full bg-[var(--bg-elevated)] border border-[var(--border-soft)] rounded-lg px-3 py-2 text-sm text-[var(--text-primary)] focus:outline-none focus:border-yellow-400" />
          <div className="flex gap-1.5 mt-2">
            {[25, 50, 75, 100].map((pct) => (
              <button key={pct} type="button" onClick={() => setMarginPct(pct)}
                className="flex-1 py-1 rounded text-xs font-mono border border-[var(--border-soft)] text-[var(--text-tertiary)] hover:border-yellow-400 hover:text-yellow-400 transition">
                {pct}%
              </button>
            ))}
          </div>
          <p className="text-xs text-[var(--text-tertiary)] mt-1">Available: ${cashBalance.toLocaleString()}</p>
        </div>



        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-mono text-[var(--text-tertiary)] uppercase mb-1 block">Stop Loss ($)</label>
            <input type="number" value={stopLoss} onChange={(e) => setStopLoss(e.target.value)} placeholder="e.g. 50"
              className="w-full bg-[var(--bg-elevated)] border border-[var(--border-soft)] rounded-lg px-3 py-2 text-sm text-[var(--text-primary)] focus:outline-none focus:border-red-400" />
            {stopLoss && marginNum > 0 && currentPrice && (
              <p className="text-xs font-mono text-[var(--text-tertiary)] mt-1">
                Closes at ${(side === "long"
                  ? currentPrice * (1 - parseFloat(stopLoss) / (marginNum * leverage))
                  : currentPrice * (1 + parseFloat(stopLoss) / (marginNum * leverage))
                ).toLocaleString(undefined, { maximumFractionDigits: 4 })}
              </p>
            )}
          </div>
          <div>
            <label className="text-xs font-mono text-[var(--text-tertiary)] uppercase mb-1 block">Take Profit ($)</label>
            <input type="number" value={takeProfit} onChange={(e) => setTakeProfit(e.target.value)} placeholder="e.g. 100"
              className="w-full bg-[var(--bg-elevated)] border border-[var(--border-soft)] rounded-lg px-3 py-2 text-sm text-[var(--text-primary)] focus:outline-none focus:border-green-400" />
            {takeProfit && marginNum > 0 && currentPrice && (
              <p className="text-xs font-mono text-[var(--text-tertiary)] mt-1">
                Closes at ${(side === "long"
                  ? currentPrice * (1 + parseFloat(takeProfit) / (marginNum * leverage))
                  : currentPrice * (1 - parseFloat(takeProfit) / (marginNum * leverage))
                ).toLocaleString(undefined, { maximumFractionDigits: 4 })}
              </p>
            )}
          </div>
        </div>

        {marginNum > 0 && (
          <div className="text-xs font-mono bg-[var(--bg-elevated)] rounded-lg p-3 space-y-1.5">
            <div className="flex justify-between"><span className="text-[var(--text-tertiary)]">Required Margin</span><span className="text-[var(--text-primary)]">${marginNum.toLocaleString()}</span></div>
            {liqPrice && <div className="flex justify-between"><span className="text-[var(--text-tertiary)]">Estimated Liquidation Price</span><span className="text-red-400">${liqPrice.toLocaleString(undefined, { maximumFractionDigits: 4 })}</span></div>}
            <div className="flex justify-between"><span className="text-[var(--text-tertiary)]">Total Position Size</span><span className="text-[var(--text-primary)]">${(marginNum * leverage).toLocaleString()}</span></div>
            <div className="flex justify-between"><span className="text-[var(--text-tertiary)]">Est. P&L (if price +10%)</span><span className="text-green-400">+${estPnl10.toLocaleString(undefined, { maximumFractionDigits: 2 })}</span></div>
          </div>
        )}

        <button type="submit" disabled={submitting || !symbol}
          className={"w-full py-3 rounded-lg font-bold uppercase tracking-wide text-sm transition disabled:opacity-50 flex items-center justify-center gap-2 " +
            (side === "long" ? "bg-green-500 hover:bg-green-600 text-white" : "bg-red-500 hover:bg-red-600 text-white")}>
          {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
          Open {side === "long" ? "Long" : "Short"}
        </button>
      </form>

    </div>
  );
}

function MonthlyChallengeTab({ assets, mode, setMode }) {
  const [me, setMe] = useState(null);
  const [leaders, setLeaders] = useState([]);
  const [champions, setChampions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [side, setSide] = useState("buy");
  const [assetType, setAssetType] = useState("crypto");
  const [symbol, setSymbol] = useState("");
  const [amountUsd, setAmountUsd] = useState("");
  const [quantity, setQuantity] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const symbolList = assetType === "crypto" ? assets.crypto : assetType === "stock" ? assets.stocks : assets.commodities;
  const owned = me?.holdings.filter((h) => h.asset_type === assetType) || [];

  const loadAll = () => {
    Promise.all([
      api.get("/monthly-challenge/me"),
      api.get("/monthly-challenge/leaderboard"),
      api.get("/monthly-challenge/champions"),
    ]).then(([m, l, c]) => {
      setMe(m.data);
      setLeaders(l.data || []);
      setChampions(c.data || []);
    }).catch(() => {}).finally(() => setLoading(false));
  };

  useEffect(() => { loadAll(); }, []);

  useEffect(() => {
    if (side === "buy" && symbolList.length > 0) setSymbol(symbolList[0]);
    else if (side === "sell" && owned.length > 0) setSymbol(owned[0].symbol);
    else setSymbol("");
  }, [side, assetType]);

  const submitTrade = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (side === "buy") {
        const amt = parseFloat(amountUsd);
        if (!amt || amt <= 0) { toast.error("Enter a valid USD amount"); return; }
        const { data } = await api.post("/monthly-challenge/buy", { symbol, asset_type: assetType, amount_usd: amt });
        toast.success(`Bought ${data.quantity} ${symbol} at $${data.price}`);
        setAmountUsd("");
      } else {
        const qty = parseFloat(quantity);
        if (!qty || qty <= 0) { toast.error("Enter a valid quantity"); return; }
        const { data } = await api.post("/monthly-challenge/sell", { symbol, asset_type: assetType, quantity: qty });
        toast.success(`Sold for $${data.proceeds} at $${data.price}`);
        setQuantity("");
      }
      loadAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Trade failed");
    } finally { setSubmitting(false); }
  };

  if (loading) return <p className="text-center text-[var(--text-tertiary)] font-mono text-sm py-10">Loading challenge...</p>;

  const monthLabel = me ? new Date(me.month + "-01").toLocaleDateString("en-US", { month: "long", year: "numeric" }) : "";
  const isUp = me?.return_pct >= 0;
  const medals = ["🥇", "🥈", "🥉"];
  const isFinalStretch = me?.days_left <= 3;
  const rankDelta = me?.rank_yesterday && me?.rank ? me.rank_yesterday - me.rank : 0;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2">
      </div>

      {mode === "spot" && (

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Trade form */}
        <form onSubmit={submitTrade} className="bg-[var(--bg-panel)] border border-[var(--border-soft)] rounded-xl p-6 space-y-4">
          <div className="flex items-center gap-2">
            <button type="button" onClick={() => setSide("buy")}
              className={"flex-1 py-2.5 rounded-lg text-xs font-bold uppercase tracking-wide transition " + (side === "buy" ? "bg-green-500 text-white" : "bg-[var(--bg-elevated)] text-[var(--text-tertiary)] border border-[var(--border-soft)]")}>
              Buy
            </button>
            <button type="button" onClick={() => setSide("sell")}
              className={"flex-1 py-2.5 rounded-lg text-xs font-bold uppercase tracking-wide transition " + (side === "sell" ? "bg-red-500 text-white" : "bg-[var(--bg-elevated)] text-[var(--text-tertiary)] border border-[var(--border-soft)]")}>
              Sell
            </button>
          </div>
          <div className="flex gap-2">
            {["crypto", "stock", "commodity"].map((t) => (
              <button key={t} type="button" onClick={() => setAssetType(t)}
                className={"flex-1 py-2 rounded-lg text-xs font-mono uppercase border transition " + (assetType === t ? "bg-yellow-400 text-black border-yellow-400" : "border-[var(--border-soft)] text-[var(--text-tertiary)]")}>
                {t}
              </button>
            ))}
          </div>
          {(side === "buy" ? symbolList : owned.map((h) => h.symbol)).length === 0 ? (
            <p className="text-xs text-[var(--text-tertiary)] py-2">{side === "sell" ? "No " + assetType + " holdings yet." : "No assets available."}</p>
          ) : (
            <select value={symbol} onChange={(e) => setSymbol(e.target.value)}
              className="w-full bg-[var(--bg-elevated)] border border-[var(--border-soft)] rounded-lg px-3 py-2.5 text-sm text-[var(--text-primary)] focus:outline-none focus:border-yellow-400">
              {(side === "buy" ? symbolList : owned.map((h) => h.symbol)).map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          )}
          {side === "buy" ? (
            <div>
              <input type="number" value={amountUsd} onChange={(e) => setAmountUsd(e.target.value)} placeholder="Amount (USD)"
                className="w-full bg-[var(--bg-elevated)] border border-[var(--border-soft)] rounded-lg px-3 py-2.5 text-sm text-[var(--text-primary)] focus:outline-none focus:border-yellow-400" />
              {me && <p className="text-xs text-[var(--text-tertiary)] mt-1">Available: ${me.cash_balance.toLocaleString()}</p>}
            </div>
          ) : (
            <input type="number" step="any" value={quantity} onChange={(e) => setQuantity(e.target.value)} placeholder="Quantity"
              className="w-full bg-[var(--bg-elevated)] border border-[var(--border-soft)] rounded-lg px-3 py-2.5 text-sm text-[var(--text-primary)] focus:outline-none focus:border-yellow-400" />
          )}
          <button type="submit" disabled={submitting || !symbol}
            className={"w-full py-3 rounded-lg font-bold uppercase tracking-wide text-sm transition disabled:opacity-50 flex items-center justify-center gap-2 " +
              (side === "buy" ? "bg-green-500 hover:bg-green-600 text-white" : "bg-red-500 hover:bg-red-600 text-white")}>
            {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
            {side === "buy" ? "Buy" : "Sell"} {symbol}
          </button>
        </form>

        {/* This month's leaderboard */}
        <div className="bg-[var(--bg-panel)] border border-[var(--border-soft)] rounded-xl overflow-hidden">
          <div className="p-4 border-b border-[var(--border-soft)] text-xs font-mono uppercase tracking-widest text-[var(--text-tertiary)]">
            {monthLabel} Standings
          </div>
          {leaders.length === 0 ? (
            <p className="text-center text-[var(--text-tertiary)] font-mono text-sm py-8">No traders yet this month.</p>
          ) : (
            leaders.slice(0, 8).map((l, i) => {
              const up = l.return_pct >= 0;
              return (
                <div key={l.user_id} className="flex items-center justify-between p-3.5 border-b border-[var(--border-soft)] last:border-0">
                  <div className="flex items-center gap-2.5">
                    <span className="text-sm w-6 text-center">{i < 3 ? medals[i] : "#" + (i + 1)}</span>
                    <span className="text-sm font-bold text-[var(--text-primary)]">{l.name}</span>
                  </div>
                  <span className={"font-mono text-xs font-bold " + (up ? "text-green-400" : "text-red-400")}>
                    {up ? "+" : ""}{l.return_pct}%
                  </span>
                </div>
              );
            })
          )}
        </div>
      </div>
      )}

      {/* Hall of fame */}
      {champions.length > 0 && (
        <div className="bg-[var(--bg-panel)] border border-[var(--border-soft)] rounded-xl overflow-hidden">
          <div className="p-4 border-b border-[var(--border-soft)] flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-[var(--text-tertiary)]">
            <Award className="w-3.5 h-3.5 text-yellow-400" /> Hall of Fame
          </div>
          {champions.map((c) => (
            <div key={c.id} className="flex items-center justify-between p-4 border-b border-[var(--border-soft)] last:border-0">
              <div className="flex items-center gap-3">
                {c.avatar_url ? (
                  <img src={c.avatar_url} alt={c.name} className="w-9 h-9 rounded-full object-cover" />
                ) : (
                  <div className="w-9 h-9 rounded-full bg-yellow-400/10 border border-yellow-400/30 flex items-center justify-center">
                    <span className="text-yellow-400 font-bold text-sm">{c.name[0]}</span>
                  </div>
                )}
                <div>
                  <div className="font-bold text-sm text-[var(--text-primary)]">{c.name}</div>
                  <div className="text-xs font-mono text-[var(--text-tertiary)]">{new Date(c.month + "-01").toLocaleDateString("en-US", { month: "long", year: "numeric" })} Champion 🏆</div>
                </div>
              </div>
              <span className="font-mono text-sm font-bold text-green-400">+{c.return_pct}%</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}



function MonthlyChallengeSidebarCard() {
  const [me, setMe] = useState(null);

  useEffect(() => {
    const load = () => api.get("/monthly-challenge/me").then((r) => setMe(r.data)).catch(() => {});
    load();
    const interval = setInterval(load, 15000);
    return () => clearInterval(interval);
  }, []);
  if (!me) return null;
  const isUp = me.return_pct >= 0;
  const monthLabel = new Date(me.month + "-01").toLocaleDateString("en-US", { month: "long", year: "numeric" });

  return (
    <div className="bg-gradient-to-br from-yellow-400/10 to-transparent border border-yellow-400/20 rounded-xl p-4">
      <div className="text-xs font-mono uppercase tracking-widest text-[var(--text-tertiary)]">{monthLabel}</div>
      <div className="font-display font-bold text-xl text-[var(--text-primary)] mt-1">
        ${me.total_value.toLocaleString(undefined, { maximumFractionDigits: 2 })}
      </div>
      <div className={"flex items-center gap-1 text-xs font-bold mt-1 " + (isUp ? "text-green-400" : "text-red-400")}>
        {isUp ? "+" : ""}{me.return_pct}% this month
      </div>
      {me.rank && (
        <div className="flex items-center gap-1.5 mt-2 text-xs font-mono">
          <span className="text-[var(--text-tertiary)]">Rank</span>
          <span className="text-yellow-400 font-bold">#{me.rank}</span>
        </div>
      )}
      <div className="flex items-center gap-1.5 mt-2 text-xs font-mono text-[var(--text-tertiary)]">
        <Clock className="w-3 h-3" /> {me.days_left} days left
      </div>
      {me.chasing && (
        <div className="mt-3 pt-3 border-t border-[var(--border-soft)] text-xs">
          <span className="text-[var(--text-secondary)]">Chasing <span className="text-[var(--text-primary)] font-bold">{me.chasing.name}</span></span>
          <div className="text-yellow-400 font-bold mt-0.5">${me.chasing.gap.toLocaleString(undefined, { maximumFractionDigits: 0 })} to catch up</div>
        </div>
      )}
    </div>
  );
}



function AccountSummaryCard({ cashBalance, equity, unrealizedPnl }) {
  return (
    <div className="bg-[var(--bg-panel)] border border-[var(--border-soft)] rounded-xl p-5">
      <div className="flex items-center gap-2 mb-3">
        <Award className="w-4 h-4 text-yellow-400" />
        <h4 className="font-mono text-xs uppercase tracking-widest text-[var(--text-tertiary)]">Your Account</h4>
      </div>
      <div className="space-y-2 text-sm font-mono">
        <div className="flex justify-between"><span className="text-[var(--text-tertiary)]">Virtual Balance</span><span className="text-[var(--text-primary)] font-bold">${cashBalance.toLocaleString(undefined, { maximumFractionDigits: 2 })}</span></div>
        <div className="flex justify-between"><span className="text-[var(--text-tertiary)]">Equity</span><span className="text-[var(--text-primary)] font-bold">${equity.toLocaleString(undefined, { maximumFractionDigits: 2 })}</span></div>
        <div className="flex justify-between"><span className="text-[var(--text-tertiary)]">Unrealized P&L</span><span className={unrealizedPnl >= 0 ? "text-green-400 font-bold" : "text-red-400 font-bold"}>{unrealizedPnl >= 0 ? "+" : ""}${unrealizedPnl.toFixed(2)}</span></div>
      </div>
    </div>
  );
}


export default function TradingSimulator() {
  const { user } = useAuth();
  const [tab, setTab] = useState("portfolio");
  const [portfolio, setPortfolio] = useState(null);
  const [assets, setAssets] = useState({ crypto: [], stocks: [], commodities: [] });
  const [loading, setLoading] = useState(true);

  // Leverage state (shared between chart column and order-form column)
  const [levMode, setLevMode] = useState("spot");
  const [levAssetType, setLevAssetType] = useState("crypto");
  const [levSymbol, setLevSymbol] = useState("");
  const [levSide, setLevSide] = useState("long");
  const [levOrderType, setLevOrderType] = useState("market");
  const [levMargin, setLevMargin] = useState("");
  const [levLeverage, setLevLeverage] = useState(10);
  const [levStopLoss, setLevStopLoss] = useState("");
  const [levTakeProfit, setLevTakeProfit] = useState("");
  const [levPositions, setLevPositions] = useState([]);
  const [levCashBalance, setLevCashBalance] = useState(0);
  const [levCurrentPrice, setLevCurrentPrice] = useState(null);
  const [levPriceChangePct, setLevPriceChangePct] = useState(null);
  const [levLatestCandle, setLevLatestCandle] = useState(null);
  const [levSubmitting, setLevSubmitting] = useState(false);
  const [levClosingId, setLevClosingId] = useState(null);
  const [levBottomTab, setLevBottomTab] = useState("position");

  const levSymbolList = levAssetType === "crypto" ? assets.crypto : levAssetType === "stock" ? assets.stocks : assets.commodities;

  const loadLevPositions = () => {
    api.get("/monthly-challenge/me").then((r) => {
      setLevPositions(r.data.leveraged_positions || []);
      setLevCashBalance(r.data.cash_balance || 0);
    }).catch(() => {});
  };

  useEffect(() => { loadLevPositions(); }, []);
  useEffect(() => {
    if (levSymbolList.length > 0) setLevSymbol(levSymbolList[0]);
  }, [levAssetType, assets]);

  useEffect(() => {
    if (!levSymbol) return;
    const fetchPrice = async () => {
      try {
        if (levAssetType === "crypto") {
          const r = await fetch("https://api.binance.com/api/v3/ticker/24hr?symbol=" + levSymbol + "USDT");
          const d = await r.json();
          setLevCurrentPrice(parseFloat(d.lastPrice));
          setLevPriceChangePct(parseFloat(d.priceChangePercent));
        } else {
          const endpoint = levAssetType === "stock" ? "/stocks/" + levSymbol : "/commodities/" + levSymbol;
          const r = await api.get(endpoint);
          setLevCurrentPrice(r.data.price);
          setLevPriceChangePct(r.data.change_pct);
        }
      } catch (_) {}
    };
    fetchPrice();
    const interval = setInterval(fetchPrice, 10000);
    return () => clearInterval(interval);
  }, [levSymbol, levAssetType]);

  useEffect(() => {
    const interval = setInterval(loadLevPositions, 15000);
    return () => clearInterval(interval);
  }, []);

  const levOpenPosition = async (e) => {
    e.preventDefault();
    const m = parseFloat(levMargin);
    if (!m || m <= 0) return toast.error("Enter a valid margin amount");
    setLevSubmitting(true);
    try {
      let slPrice = null;
      let tpPrice = null;
      if (levCurrentPrice) {
        if (levStopLoss) {
          const lossPct = parseFloat(levStopLoss) / (m * levLeverage);
          slPrice = levSide === "long" ? levCurrentPrice * (1 - lossPct) : levCurrentPrice * (1 + lossPct);
        }
        if (levTakeProfit) {
          const profitPct = parseFloat(levTakeProfit) / (m * levLeverage);
          tpPrice = levSide === "long" ? levCurrentPrice * (1 + profitPct) : levCurrentPrice * (1 - profitPct);
        }
      }
      await api.post("/monthly-challenge/leverage/open", {
        symbol: levSymbol, asset_type: levAssetType, side: levSide, margin: m, leverage: levLeverage,
        stop_loss: slPrice,
        take_profit: tpPrice,
      });
      toast.success(levSide.toUpperCase() + " position opened on " + levSymbol + " at " + levLeverage + "x");
      setLevMargin("");
      setLevStopLoss("");
      setLevTakeProfit("");
      loadLevPositions();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not open position");
    } finally { setLevSubmitting(false); }
  };

  const levClosePosition = async (id) => {
    setLevClosingId(id);
    try {
      const { data } = await api.post("/monthly-challenge/leverage/close/" + id);
      const isProfit = data.pnl >= 0;
      toast.success((isProfit ? "Closed with +$" : "Closed with -$") + Math.abs(data.pnl).toFixed(2) + " P&L");
      loadLevPositions();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not close position");
    } finally { setLevClosingId(null); }
  };

  const loadPortfolio = () => {
    if (!user) return;
    api.get("/virtual-portfolio/me").then((r) => setPortfolio(r.data)).catch(() => {}).finally(() => setLoading(false));
  };

  useEffect(() => {
    api.get("/trading/assets").then((r) => setAssets(r.data)).catch(() => {});
    loadPortfolio();
  }, [user?.id]);


  return (
    <div className="pt-5 pb-20">
      <div className="mx-auto px-6 md:px-10 max-w-[1800px]">
                <div className="mb-4" />

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Left column — cards only, no nav buttons */}
          <div className="lg:col-span-2 space-y-4">
            <AccountSummaryCard
              cashBalance={levCashBalance}
              equity={levCashBalance + levPositions.reduce((sum, p) => sum + (p.value || p.margin), 0)}
              unrealizedPnl={levPositions.reduce((sum, p) => sum + (p.pnl || 0), 0)}
            />
            <MonthlyChallengeSidebarCard />
          </div>

          {/* Main content */}
          <div className="lg:col-span-7">
            <LeverageMainContent
              assetType={levAssetType} symbol={levSymbol} positions={levPositions}
              bottomTab={levBottomTab} setBottomTab={setLevBottomTab}
              closePosition={levClosePosition} closingId={levClosingId}
              currentPrice={levCurrentPrice} priceChangePct={levPriceChangePct}
              latestCandle={levLatestCandle} setLatestCandle={setLevLatestCandle}
            />
          </div>

          {/* Right rail */}
          <div className="lg:col-span-3">
            <LeverageOrderPanel
              assetType={levAssetType} setAssetType={setLevAssetType}
              symbol={levSymbol} setSymbol={setLevSymbol} symbolList={levSymbolList}
              side={levSide} setSide={setLevSide}
              orderType={levOrderType} setOrderType={setLevOrderType}
              margin={levMargin} setMargin={setLevMargin}
              leverage={levLeverage} setLeverage={setLevLeverage}
              stopLoss={levStopLoss} setStopLoss={setLevStopLoss}
              takeProfit={levTakeProfit} setTakeProfit={setLevTakeProfit}
              cashBalance={levCashBalance} currentPrice={levCurrentPrice}
              unrealizedPnl={levPositions.reduce((sum, p) => sum + (p.pnl || 0), 0)}
              equity={levCashBalance + levPositions.reduce((sum, p) => sum + (p.value || p.margin), 0)}
              submitting={levSubmitting} onSubmit={levOpenPosition}
            />
          </div>
        </div>

        <p className="text-xs text-[var(--text-tertiary)] text-center mt-10">
          Virtual trading uses live market prices but no real money is involved. Not financial advice.
        </p>
      </div>
    </div>
  );
}